import React, { useEffect, useRef, useState } from "react";

/**
 * A small dropdown for Signals (the app never uses a native <select>): a button that opens a menu of options,
 * with a search box when `searchable`. options: [{value, label, hint?, tag?, group?}]; options sharing a `group` sit
 * under one heading (pass them already in group order). `emptyText` shows when there are no options at all.
 * `buttonLabel` replaces the button's text (a "Change" link that opens the menu).
 */
const SignalPicker = ({ value, options, onChange, placeholder = "Choose…", searchable = false, prefix = "", className = "", emptyText = "", buttonLabel = "", defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  const [query, setQuery] = useState("");
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const close = e => ref.current && !ref.current.contains(e.target) && setOpen(false);
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  const current = options.find(o => o.value === value);
  const q = query.trim().toLowerCase();
  const shown = q ? options.filter(o => `${o.label} ${o.hint || ""}`.toLowerCase().includes(q)) : options;
  const pick = v => {
    onChange(v);
    setOpen(false);
    setQuery("");
  };

  return (
    <div className={`sg-pick ${className}`} ref={ref}>
      <button type="button" className={`sg-pick__btn ${open ? "is-open" : ""}`} onClick={() => setOpen(!open)} aria-haspopup="listbox" aria-expanded={open}>
        {buttonLabel ? <span>{buttonLabel}</span> : <span className={current ? "" : "sg-pick__ph"}>{prefix}{current ? current.label : placeholder}</span>}
        {!buttonLabel && <i className="sg-pick__caret" aria-hidden="true" />}
      </button>
      {open && (
        <div className="sg-pick__menu" role="listbox">
          {searchable && (
            <input className="sg-input sg-pick__search" autoFocus placeholder="Search" value={query} onChange={e => setQuery(e.target.value)} />
          )}
          {shown.map((o, i) => (
            <React.Fragment key={String(o.value)}>
              {o.group && o.group !== (shown[i - 1] || {}).group && <div className="sg-pick__group">{o.group}</div>}
              <button type="button" role="option" aria-selected={o.value === value}
                className={`sg-pick__opt ${o.value === value ? "on" : ""}`} onClick={() => pick(o.value)}>
                <span>{o.label}{o.tag && <em className="sg-pick__tag">{o.tag}</em>}</span>
                {o.hint && <small>{o.hint}</small>}
              </button>
            </React.Fragment>
          ))}
          {shown.length === 0 && <div className="sg-pick__none">{!q && emptyText ? emptyText : "Nothing matches"}</div>}
        </div>
      )}
    </div>
  );
};

export default SignalPicker;
